/**
 * PremiumVehicleIcons — Phase 7g.15.28
 *
 * Shared Tron-style vehicle iconography and selection-card component.
 * Extracted from `PremiumHomePage.jsx` so the same visual treatment can
 * be reused across the customer portal (saved vehicles, add-vehicle
 * dialog, multi-vehicle booking summary) without re-implementing the
 * design.
 *
 * The icons are pure-SVG `currentColor` line drawings — they take their
 * color from the parent's text color via Tailwind utilities. Each
 * vehicle type carries its own accent color + gradient so the booking
 * flow feels like a single coordinated visual language regardless of
 * which surface the user is on (public home page or logged-in portal).
 */
import React from "react";
import { useTranslation } from "react-i18next";

// ── SVG icon components ────────────────────────────────────────────
// Each accepts an arbitrary `className` so callers control sizing.
// All strokes are 1.5px on a 24×24 viewBox for visual consistency.

export const CarSuvIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M5 17H3a2 2 0 01-2-2v-3a2 2 0 012-2h1l2.5-4h9L16 10h1a2 2 0 012 2v3a2 2 0 01-2 2h-2" />
    <circle cx="7.5" cy="17" r="2" />
    <circle cx="16.5" cy="17" r="2" />
  </svg>
);

export const BoatIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M3 17h18l-2-5H5L3 17z" />
    <path d="M9 12V8h4v4" />
    <path d="M11 8V4" />
    <path d="M9 4h4" />
    <path d="M2 20c2-1.5 4-1.5 6 0s4 1.5 6 0 4-1.5 6 0" />
  </svg>
);

export const RvIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="1" y="6" width="17" height="11" rx="2" />
    <path d="M18 9h3l2 4v4h-5V9z" />
    <circle cx="5.5" cy="17" r="1.5" />
    <circle cx="13.5" cy="17" r="1.5" />
    <circle cx="20.5" cy="17" r="1.5" />
  </svg>
);

export const FleetIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M1 4h14v11H1z" />
    <path d="M15 8h4l3 4v3h-7V8z" />
    <circle cx="4.5" cy="17.5" r="1.5" />
    <circle cx="11.5" cy="17.5" r="1.5" />
    <circle cx="19.5" cy="17.5" r="1.5" />
  </svg>
);

export const GolfCartIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="4" y="4" width="7" height="3" rx="1" />
    <path d="M4 7v3l3 3h7l2-4H4z" />
    <path d="M4 13h16" />
    <circle cx="6.5" cy="17" r="2" />
    <circle cx="15.5" cy="17" r="2" />
  </svg>
);

export const RecreationalIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="5" cy="17" r="3" />
    <circle cx="19" cy="17" r="3" />
    <path d="M5 17h14" />
    <path d="M14 7h2l1.5 3.5" />
    <path d="M14 7l-1.5-2.5h-3L8 7l-1 4h9l-2-4z" />
  </svg>
);

export const LuxuryIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M3 14l1.5-4.5A2 2 0 0 1 6.4 8h11.2a2 2 0 0 1 1.9 1.5L21 14" />
    <path d="M3 14v4a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1v-1h10v1a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1v-4H3z" />
    <circle cx="7" cy="14" r="1.5" />
    <circle cx="17" cy="14" r="1.5" />
    <path d="M12 4l1.5 3h-3L12 4z" fill="currentColor" />
  </svg>
);

// Phase 7g.15.47 — small aircraft cosmetic-detailing only. NOT
// FAA-certified / NOT maintenance / NOT airworthiness work.
// Disclaimer copy lives in `vehiclePricing.aircraft.disclaimer` and
// in the PackageStep render path so customers see it at every
// surface where aircraft is selectable.
export const AircraftIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M12 2l3 8 7 3-7 1-2 8-1-8-7-1 7-3 0-8z" />
  </svg>
);

// ── Canonical config ───────────────────────────────────────────────
// `id` matches the canonical `vehicle_type` value used throughout the
// app (URL params, backend schemas, booking flow constants). DO NOT
// rename without updating callers — backend `pricing_canonical`
// keys on these exact strings.

export const VEHICLE_TYPES = [
  { id: "car",          icon: CarSuvIcon,      labelKey: "vehicle_types.car_suv",            color: "cyan",    gradient: "from-cyan-500 to-blue-600" },
  { id: "boat",         icon: BoatIcon,        labelKey: "vehicle_types.boat_marine",        color: "blue",    gradient: "from-blue-500 to-indigo-600" },
  { id: "rv",           icon: RvIcon,          labelKey: "vehicle_types.rv",                 color: "purple",  gradient: "from-purple-500 to-pink-600" },
  { id: "fleet",        icon: FleetIcon,       labelKey: "vehicle_types.fleet",              color: "amber",   gradient: "from-amber-500 to-orange-600" },
  { id: "recreational", icon: RecreationalIcon, labelKey: "vehicle_types.motorcycle_rec",    color: "orange",  gradient: "from-orange-500 to-red-600" },
  { id: "golf-cart",    icon: GolfCartIcon,    labelKey: "vehicle_types.golf_cart",          color: "emerald", gradient: "from-emerald-500 to-teal-600" },
  { id: "luxury",       icon: LuxuryIcon,      labelKey: "vehicle_types.luxury_collectible", color: "yellow",  gradient: "from-yellow-400 via-amber-500 to-orange-500" },
  // Phase 7g.15.88 (Jun 2026) — Rick QA reorder request:
  // Car → Marine → RV/Camper → Fleet → Recreational Vehicles → Golf Carts
  // → Luxury Collectibles → Small Engine Aircraft.
  // `fullWidth` dropped so the 8 items form a clean uniform grid
  // (2 rows × 4 cols on phone vertical) with no awkward empty cells.
  // Phase 7g.15.47 — Small aircraft cosmetic detailing only.
  // ALWAYS routes to custom quote + admin/vendor review.
  { id: "aircraft",     icon: AircraftIcon,    labelKey: "vehicle_types.small_aircraft",     color: "sky",     gradient: "from-sky-400 via-cyan-500 to-blue-600", customQuoteOnly: true },
];

// `classic` is treated as a stylistic alias of `luxury` in the UI —
// the legal/pricing layer distinguishes them, but the iconography
// is shared. Customers see the same Crown/Luxury treatment.
const ALIASES = { classic: "luxury" };

const _CONFIG_BY_ID = Object.fromEntries(VEHICLE_TYPES.map((v) => [v.id, v]));

/**
 * Look up the canonical icon + color + gradient + label for a given
 * vehicle_type string. Returns a stable fallback (Car / SUV) for any
 * unknown id so callers never crash on stale data.
 */
export function getVehicleConfig(vehicleType) {
  const id = ALIASES[vehicleType] || vehicleType || "car";
  return _CONFIG_BY_ID[id] || _CONFIG_BY_ID.car;
}

// ── Reusable selectable card ───────────────────────────────────────
// Matches the home-page treatment exactly: subtle slate background
// when idle, a gradient-filled glow when selected, hover lift, and
// the icon swap between accent-tinted and white. Accepts arbitrary
// children for callers that want to add extra metadata below the
// icon + label (e.g. "Primary" star, length, etc.).

/**
 * @param {object} props
 * @param {string} props.vehicleType
 * @param {boolean} props.selected
 * @param {() => void} props.onSelect
 * @param {string=} props.testId
 * @param {string=} props.size — "sm" (compact, used inside dialogs) or "md" (home hero default)
 * @param {React.ReactNode=} props.children — optional extra row(s) below label
 * @param {boolean=} props.compact — render a horizontal layout (used by 1-row pickers)
 */
export const PremiumVehicleCard = ({
  vehicleType,
  selected,
  onSelect,
  testId,
  size = "md",
  children,
  compact = false,
}) => {
  const { t } = useTranslation();
  const cfg = getVehicleConfig(vehicleType);
  const Icon = cfg.icon;

  const iconBoxSizing =
    size === "sm"
      ? "w-9 h-9"
      : "w-10 h-10 sm:w-14 sm:h-14";
  const iconSizing =
    size === "sm"
      ? "w-5 h-5"
      : "w-5 h-5 sm:w-7 sm:h-7";
  const labelSizing =
    size === "sm"
      ? "text-[11px] sm:text-xs font-medium"
      : "text-xs sm:text-sm font-medium";

  return (
    <button
      type="button"
      onClick={onSelect}
      data-testid={testId || `vehicle-type-${vehicleType}`}
      data-vehicle-type={vehicleType}
      data-selected={selected ? "true" : "false"}
      className={`group ${
        compact
          ? "flex flex-row items-center gap-3 py-2.5 px-3"
          : "flex flex-col items-center gap-2 p-3 sm:p-4"
      } rounded-2xl transition-all duration-300 ${
        selected
          ? `bg-gradient-to-br ${cfg.gradient} shadow-xl shadow-${cfg.color}-500/30`
          : "bg-slate-900/50 hover:bg-slate-800/50 border border-white/10 hover:border-white/20"
      }`}
    >
      <div
        className={`${iconBoxSizing} rounded-xl flex items-center justify-center transition-all duration-300 flex-shrink-0 ${
          selected
            ? "bg-white/20"
            : `bg-${cfg.color}-500/20 group-hover:bg-${cfg.color}-500/30`
        }`}
      >
        <Icon
          className={`${iconSizing} ${
            selected ? "text-white" : `text-${cfg.color}-400`
          }`}
        />
      </div>
      <span
        className={`${labelSizing} leading-tight ${
          compact ? "text-left flex-1" : "text-center"
        } ${selected ? "text-white" : "text-slate-300 group-hover:text-white"}`}
      >
        {t(cfg.labelKey)}
      </span>
      {children}
    </button>
  );
};

/**
 * Pure-display variant — same iconography as `PremiumVehicleCard`
 * but never clickable + ignores `selected` state. Used in vehicle-list
 * rows (saved vehicles, multi-vehicle summary) where the user already
 * picked the vehicle and we're just rendering it back.
 *
 * @param {string} props.vehicleType
 * @param {"sm"|"md"|"lg"} props.size
 */
export const PremiumVehicleAvatar = ({ vehicleType, size = "md", className = "" }) => {
  const cfg = getVehicleConfig(vehicleType);
  const Icon = cfg.icon;

  const containerSize = {
    sm: "w-10 h-10",
    md: "w-12 h-12",
    lg: "w-14 h-14",
  }[size] || "w-12 h-12";

  const iconSize = {
    sm: "w-5 h-5",
    md: "w-6 h-6",
    lg: "w-7 h-7",
  }[size] || "w-6 h-6";

  return (
    <div
      data-testid={`vehicle-avatar-${vehicleType}`}
      data-vehicle-type={vehicleType}
      className={`${containerSize} rounded-xl flex items-center justify-center bg-gradient-to-br ${cfg.gradient} shadow-lg shadow-${cfg.color}-500/30 ${className}`}
    >
      <Icon className={`${iconSize} text-white`} />
    </div>
  );
};

export default {
  CarSuvIcon,
  BoatIcon,
  RvIcon,
  FleetIcon,
  GolfCartIcon,
  RecreationalIcon,
  LuxuryIcon,
  AircraftIcon,
  VEHICLE_TYPES,
  getVehicleConfig,
  PremiumVehicleCard,
  PremiumVehicleAvatar,
};
